// src/pages/FinancePage/components/funnel-detail/FunnelTable.tsx

import { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { UnitConceptRow } from './types';
import {
  formatCurrency, formatPercentage,
  getSeverity, brechaTextColor,
  calculateUnitValues,
} from './utils';

type Props = {
  unitRows: UnitConceptRow[];
  expectedProfitPct: number;
  realDeliveries: number;       // # entregas reales del período (de FunnelStages)
  initialProjectedDeliveries: number;  // sugerencia inicial = realDeliveries
};

export function FunnelTable({ unitRows, expectedProfitPct, realDeliveries, initialProjectedDeliveries }: Props) {
  const [projectedDeliveries, setProjectedDeliveries] = useState(initialProjectedDeliveries);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialProjectedDeliveries.toString());

  useEffect(() => { if (!editing) setDraft(projectedDeliveries.toString()); }, [projectedDeliveries, editing]);

  const realUnit = calculateUnitValues(unitRows, false);
  const targetUnit = calculateUnitValues(unitRows, true, expectedProfitPct);

  const get = (id: UnitConceptRow['id']): number => {
    const row = unitRows.find(r => r.id === id);
    return row ? (row.realValue ?? 0) : 0;
  };

  // Reales: por orden × # entregas reales
  const realRevenue = realUnit.effectivePrice * realDeliveries;
  const realProductCost = get('unit-product-cost') * realDeliveries;
  const realFreight = get('unit-shipping-cost') * realDeliveries;
  const realReturns = realUnit.returnsAmount * realDeliveries;
  const realCpa = get('unit-cpa') * realDeliveries;
  const realTotalCosts = realProductCost + realFreight + realReturns;
  const realProfit = realRevenue - realTotalCosts - realCpa;

  // Objetivo: por orden objetivo × # entregas proyectadas
  const targetRevenue = targetUnit.effectivePrice * projectedDeliveries;
  const targetTotalCosts = targetUnit.totalCosts * projectedDeliveries;
  const targetCpa = (unitRows.find(r => r.id === 'unit-cpa')?.targetValue ?? 0) * projectedDeliveries;
  const targetProfit = targetRevenue - targetTotalCosts - targetCpa;

  const pctOf = (n: number, base: number) => base > 0 ? (n / base) * 100 : 0;

  const commit = () => {
    const parsed = parseInt(draft.replace(/[^\d]/g, ''));
    if (!isNaN(parsed) && parsed > 0) setProjectedDeliveries(parsed);
    setEditing(false);
  };

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white tabular-nums">
      <header className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Resultado del embudo</h2>
          <p className="text-xs text-gray-500 mt-0.5">Acumulado del período. Los valores objetivo se proyectan según las entregas que esperas alcanzar.</p>
        </div>

        <div className="inline-flex shrink-0 flex-col items-center rounded-lg border border-blue-100 bg-blue-50 px-5 py-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#2b5ce6]">Proyectar</p>
          {editing ? (
            <input
              autoFocus
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setEditing(false);
              }}
              className="w-20 rounded border border-blue-200 bg-white px-1 text-center text-xl font-semibold tabular-nums text-gray-900 outline-none focus:border-[#2b5ce6]"
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-baseline gap-1.5 rounded px-2 py-0.5 text-xl font-semibold text-gray-900 transition hover:bg-blue-100"
            >
              <span>{projectedDeliveries} <span className="text-sm font-normal text-[#2b5ce6]">entregas</span></span>
              <Pencil className="w-3 h-3 self-center text-[#2b5ce6]" />
            </button>
          )}
        </div>
      </header>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Concepto</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Valor real</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Objetivo</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold w-32">Diferencia</th>
          </tr>
        </thead>
        <tbody>
          <RowBlock
            label="Recaudo del embudo"
            sublabel={`${realDeliveries} entregas reales`}
            colorScheme="green"
            realValue={realRevenue}
            targetValue={targetRevenue}
            realPct={100}
            targetPct={100}
            isCost={false}
          />
          <RowBlock
            label="Costos totales"
            colorScheme="red"
            realValue={realTotalCosts}
            targetValue={targetTotalCosts}
            realPct={pctOf(realTotalCosts, realRevenue)}
            targetPct={pctOf(targetTotalCosts, targetRevenue)}
            isCost={true}
          />
          <RowBlock
            label="CPA"
            colorScheme="blue"
            realValue={realCpa}
            targetValue={targetCpa}
            realPct={pctOf(realCpa, realRevenue)}
            targetPct={pctOf(targetCpa, targetRevenue)}
            isCost={true}
          />
          <ProfitBannerRow
            realValue={realProfit}
            targetValue={targetProfit}
            realPct={pctOf(realProfit, realRevenue)}
            targetPct={pctOf(targetProfit, targetRevenue)}
          />
        </tbody>
      </table>
    </section>
  );
}

function RowBlock({ label, sublabel, colorScheme, realValue, targetValue, realPct, targetPct, isCost }: {
  label: string;
  sublabel?: string;
  colorScheme: 'red' | 'green' | 'blue';
  realValue: number;
  targetValue: number;
  realPct: number;
  targetPct: number;
  isCost: boolean;
}) {
  const styles = {
    red: { dot: 'bg-red-500', text: 'text-red-700' },
    green: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    blue: { dot: 'bg-[#2b5ce6]', text: 'text-[#2b5ce6]' },
  }[colorScheme];

  const ppGap = realPct - targetPct;
  const severity = getSeverity(realValue, targetValue, isCost);

  return (
    <tr className="border-t border-gray-100 bg-white hover:bg-gray-50/50">
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${styles.dot}`} />
          <span className="font-semibold text-gray-900">{label}</span>
        </div>
        {sublabel && <div className="text-[11px] text-gray-500 mt-0.5">{sublabel}</div>}
      </td>
      <td className="text-right px-3 py-3.5">
        <div className={`font-semibold ${styles.text}`}>{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-3.5">
        <div className="font-semibold text-gray-700">{formatCurrency(targetValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(targetPct)}</div>
      </td>
      <td className={`text-right px-3 py-3.5 font-medium ${brechaTextColor(severity)}`}>
        {Math.abs(ppGap) >= 0.05
          ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
          : '—'}
      </td>
    </tr>
  );
}

function ProfitBannerRow({ realValue, targetValue, realPct, targetPct }: {
  realValue: number;
  targetValue: number;
  realPct: number;
  targetPct: number;
}) {
  const ppGap = realPct - targetPct;
  const severity = getSeverity(realValue, targetValue, false);
  const rowClass = realValue < 0 ? "bg-red-50/80 border-red-100" : "bg-emerald-50/70 border-emerald-100";
  const valueClass = realValue < 0 ? "text-red-800" : "text-emerald-800";

  return (
    <tr className={`border-t-2 ${rowClass}`}>
      <td className="px-4 py-4">
        <div className="font-bold text-gray-900 text-base">Utilidad del embudo</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className={`font-bold text-base ${valueClass}`}>{formatCurrency(realValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(realPct)}</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className="font-bold text-base text-gray-900">{formatCurrency(targetValue)}</div>
        <div className="text-[11px] text-gray-500">{formatPercentage(targetPct)}</div>
      </td>
      <td className="text-right px-3 py-4">
        <div className={`font-semibold ${brechaTextColor(severity)}`}>
          {Math.abs(ppGap) >= 0.05
            ? `${ppGap > 0 ? '+' : '−'}${Math.abs(ppGap).toFixed(1)}%`
            : '—'}
        </div>
      </td>
    </tr>
  );
}
